import React, { useState } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import Constants from "../../utils/Constants";
import SplashScreen from "../../components/loader/SplashScreen/SplashScreen";
import { useEffect } from "react";
import SearchHead from "../../components/custom/SearchHead";
import ReactPaginate from "react-paginate";
import UIkit from "uikit";
import { usePermissions } from "../../context/PermissionsContext";
import { Play, Stop, Trash, TrashCan, View } from "@carbon/icons-react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const CampaignsList = () => {
  const [page, setPage] = useState(1);
  const [loader, Setloader] = useState(false);
  const [searchparam, setsearchparam] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();

  const { Permissions } = usePermissions();

  const CampaignsApi = (page) => {
    return axios({
      method: "get",
      params: {
        page: page || 1,
        limit: 100,
        ...(searchparam ? { search: searchparam } : {}),
        ...(startDate ? { 'createdAt[gte]': startDate } : {}),
        ...(endDate ? { 'createdAt[lte]': endDate } : {}),
      },
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      url: Constants.url + "survey-campaigns",
    }).then((res) => {
      return res.data.data;
    });
  };

  const {
    data: CampaignsData,
    isLoading,
    refetch,
  } = useQuery(
    {
      queryKey: ["campaigns", page, searchparam, startDate, endDate],
      queryFn: () => CampaignsApi(page),
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 60,
    }
  );

  const Campaigns = CampaignsData?.campaigns || [];

  useEffect(() => {
    refetch();
  }, [searchparam, startDate, endDate]);

  const Refresh = () => {
    Setloader(true);
    refetch().then(() => {
      Setloader(false);
    });
  };

  const handleToggleCampaign = async (campaign) => {
    const action = campaign.isActive ? "stop" : "start";
    const endpoint = campaign.isActive 
      ? `survey-campaigns/${campaign.id}/stop`
      : `survey-campaigns/${campaign.id}/start`;

    Swal.fire({
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Campaign?`,
      text: `Are you sure you want to ${action} "${campaign.campaignName}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: campaign.isActive ? "#dc3545" : "#28a745",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${action} it!`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Setloader(true);
          await axios.patch(`${Constants.url}${endpoint}`, {}, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });

          Swal.fire(
            "Success",
            `Campaign ${action}ed successfully`,
            "success"
          );
          refetch();
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || `Failed to ${action} campaign`;
          Swal.fire("Error", errorMessage, "error");
        } finally {
          Setloader(false);
        }
      }
    });
  };

  const handleDeleteCampaign = async (campaign) => {
    Swal.fire({
      title: "Delete Campaign?",
      text: `Are you sure you want to delete "${campaign.campaignName}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Setloader(true);
          await axios.delete(
            `${Constants.url}survey-campaigns/${campaign.id}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );

          Swal.fire("Deleted", "Campaign deleted successfully", "success");
          refetch();
        } catch (error) {
          const errorMessage =
            error.response?.data?.message || "Failed to delete campaign";
          Swal.fire("Error", errorMessage, "error");
        } finally {
          Setloader(false);
        }
      }
    });
  };

  const getChannelBadges = (channels) => {
    return channels.map((channel) => (
      <span key={channel} className="campaign-channel-badge">
        {channel.toUpperCase()}
      </span>
    ));
  };

  return (
    <>
      {!isLoading && !loader ? (
        <div className="dashboardWrp uk-padding">
          <div className="uk-container uk-container-expand">
            <div className="uk-margin">
              <div className="uk-flex uk-flex-between">
                <div>
                  <h2>Survey Campaigns</h2>
                </div>
              </div>
            </div>

            <div className="analyticsTableWrp uk-overflow-auto">
              <table className="uk-table uk-table-striped">
                <thead className="table-header-shadow">
                  <tr className="uk-text-center">
                    <th className="uk-text-center">S.NO</th>
                    <th className="uk-text-left">Campaign Name</th>
                    <th className="uk-text-center">Channels</th>
                    <th className="uk-text-center">Trigger Type</th>
                    <th className="uk-text-center">Status</th>
                    <th className="uk-text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {Campaigns?.map((campaign, index) => (
                    <tr key={campaign.id}>
                      <td className="uk-text-center">
                        {(page - 1) * 100 + (index + 1)}
                      </td>
                      <td className="uk-text-left">
                        <div>
                          <p style={{ margin: "0 0 4px 0", fontWeight: "600" }}>
                            {campaign?.campaignName || "-"}
                          </p>
                          <p style={{ margin: "0", fontSize: "12px", color: "#666" }}>
                            {campaign?.description || "-"}
                          </p>
                        </div>
                      </td>
                      <td className="uk-text-center">
                        <div style={{ display: "flex", gap: "6px", justifyContent: "center", flexWrap: "wrap" }}>
                          {getChannelBadges(campaign?.distributionChannels || [])}
                        </div>
                      </td>
                      <td className="uk-text-center">
                        <span className="campaign-trigger-badge">
                          {campaign?.triggerType || "-"}
                        </span>
                      </td>
                      <td className="uk-text-center">
                        <span className={`campaign-status-badge ${campaign?.isActive ? 'active' : 'inactive'}`}>
                          {campaign?.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="uk-text-center">
                        <div className="campaign-actions-group">
                          <button
                            className={`campaign-action-btn ${
                              campaign?.isActive
                                ? "campaign-action-btn--stop"
                                : "campaign-action-btn--start"
                            }`}
                            title={campaign?.isActive ? "Stop Campaign" : "Start Campaign"}
                            onClick={() => handleToggleCampaign(campaign)}
                            disabled={loader}
                          >
                            {campaign?.isActive ? (
                              <>
                                <Stop size={16} /> Stop
                              </>
                            ) : (
                              <>
                                <Play size={16} /> Start
                              </>
                            )}
                          </button>
                          <button
                            className="campaign-action-btn campaign-action-btn--responses"
                            title="View Responses"
                            onClick={() => navigate(`/campaign-responses/${campaign.id}`)}
                            disabled={loader}
                          >
                            <View size={16} />
                            Responses
                          </button>
                          <button
                            className="campaign-action-btn campaign-action-btn--responses"
                            title="View Responses"
                            onClick={() => navigate(`/campaign-recepients/${campaign.id}`)}
                            disabled={loader}
                          >
                            <View size={16} />
                            Recepients
                          </button>
                          {/* <button
                            className="campaign-action-btn campaign-action-btn--delete"
                            title="Delete Campaign"
                            onClick={() => handleDeleteCampaign(campaign)}
                            disabled={loader}
                          >
                            <TrashCan size={16} />
                            Delete
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {Campaigns?.length === 0 && (
                <div className="uk-padding uk-text-center">
                  <p style={{ color: "#999", fontSize: "14px" }}>
                    No campaigns found
                  </p>
                </div>
              )}
              <div className="paginationcard uk-card uk-card-default uk-margin-remove-top">
                <ReactPaginate
                  previousLabel={"←"}
                  nextLabel={"→"}
                  breakLabel={"..."}
                  pageCount={Math.ceil((CampaignsData?.total || 0) / 100)}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={(e) => setPage(e.selected + 1)}
                  containerClassName={"pagination"}
                  activeClassName={"active"}
                />
              </div>
            </div>
          </div>
        </div>
      ) : (
        <SplashScreen />
      )}
    </>
  );
};

export default CampaignsList;
